import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { safeDivide } from 'src/utils/app';
import useAtlasV2DorisColumnData from 'src/serverProxy/useAtlasV2DorisColumnData';
import { useDorisEntityDropdownOptions } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficDoubleDropdownTopEntity';
import {
  DorisDropdownFieldNameEnum,
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { promotionMapping } from 'src/components/AtlasV2Components/AtlasPromotionsV2PageComponents/promotionMapping';

const fieldOptions = [
  {
    label: 'Promotion Count',
    id: DorisMetricTypeEnum.PromotionCount,
    fieldName: DorisMetricTypeEnum.PromotionCount,
    metricType: METRICTYPE.VOLUME
  },
  {
    label: 'Promotion Sales',
    id: DorisMetricTypeEnum.PromotionSales,
    fieldName: DorisMetricTypeEnum.PromotionSales,
    metricType: METRICTYPE.MONEY
  },
  {
    label: 'Share of Retail Sales',
    id: DorisMetricTypeEnum.PromotionSalesRetailSales,
    fieldName: DorisMetricTypeEnum.PromotionSales,
    marketMetric: DorisMetricTypeEnum.RetailSales,
    metricType: METRICTYPE.PERCENT
  },
  {
    label: 'Average Discount',
    id: DorisMetricTypeEnum.AverageDiscount,
    fieldName: DorisMetricTypeEnum.AverageDiscount,
    metricType: METRICTYPE.PERCENT
  }
];

export const useRetailSalesData = ({ currentMetricOption, selectedGroupByFieldId }) => {
  const { data: targetData, isLoading: isTargetLoading } = useAtlasV2DorisColumnData({
    metricType: currentMetricOption.fieldName,
    fieldName: selectedGroupByFieldId,
    queryKey: 'Atlas-retailSales-target',
    endPoint: DorisMetricEndPointEnum.SalesEndPoint
  });

  const { data: marketShareData, isLoading: isMarketShareLoading } = useAtlasV2DorisColumnData({
    metricType: currentMetricOption.marketMetric,
    fieldName: selectedGroupByFieldId,
    queryKey: 'Atlas-retailSales-market',
    endPoint: DorisMetricEndPointEnum.SalesEndPoint,
    enableFetch: !!currentMetricOption.marketMetric
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    const currentMarketShareMap =
      marketShareData?.reduce((acc, item) => {
        acc[item.fieldId] = item.values?.[selectedGroupByFieldId] || 0;
        return acc;
      }, {}) || {};

    const compareMarketShareMap =
      marketShareData?.reduce((acc, item) => {
        acc[item.fieldId] = item.comparisonValues?.[selectedGroupByFieldId] || 0;
        return acc;
      }, {}) || {};

    targetData?.forEach((datum) => {
      const rawName = datum?.metadata?.name;
      const datumName = promotionMapping[rawName] || rawName;
      const datumId = datum?.fieldId;

      categoryData.push({
        name: datumName,
        id: datumId,
        type: selectedGroupByFieldId
      });

      const currentDatum = datum?.values?.[selectedGroupByFieldId] ?? 0;
      const comparisonDatum = datum?.comparisonValues?.[selectedGroupByFieldId] ?? 0;

      // if there is share, calculate market share, otherwise use metric value
      const currentRow = !!currentMetricOption.marketMetric
        ? safeDivide(currentDatum, currentMarketShareMap[datumId])
        : Math.abs(currentDatum);

      const comparisonRow = !!currentMetricOption.marketMetric
        ? safeDivide(comparisonDatum, compareMarketShareMap[datumId])
        : Math.abs(comparisonDatum);

      currentData.push([datumName, currentRow]);
      comparisonData.push([datumName, comparisonRow]);
    });

    return { currentData, comparisonData, categoryData };
  }, [currentMetricOption.marketMetric, marketShareData, selectedGroupByFieldId, targetData]);

  return {
    currentData: parsedData.currentData,
    comparisonData: parsedData.comparisonData,
    categoryData: parsedData.categoryData,
    isLoading: isTargetLoading || (currentMetricOption.marketMetric ? isMarketShareLoading : false)
  };
};

/**
 *
 * Promotions double dropdown
 */
const AtlasV2PromotionsDoubleDropdownTopEntityChart = ({
  groupByFieldOptions
}: {
  groupByFieldOptions?: { id: string; label: string }[];
}) => {
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const defaultGroupByOptions = useDorisEntityDropdownOptions(DorisDropdownFieldNameEnum.Sales);
  const groupByOptions = groupByFieldOptions || defaultGroupByOptions;

  const [selectedMainMetricField, setSelectedMainMetricField] = useState(DorisMetricTypeEnum.PromotionCount);

  const [selectedGroupByFieldId, setSelectedGroupByFieldId] = useState(groupByOptions[0].id);
  const currentGroupByOption =
    groupByOptions.find((option) => option.id === selectedGroupByFieldId) || groupByOptions[0];

  const currentMetricOption = fieldOptions.find((option) => option.id === selectedMainMetricField);

  const { currentData, comparisonData, categoryData, isLoading } = useRetailSalesData({
    currentMetricOption,
    selectedGroupByFieldId
  });

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const label = `Promotion ${currentMetricOption.label} by ${currentGroupByOption.label}`;

        return saveBarChartAsDelimitedData(currentData, comparisonData, {
          fileTitle: `Promotion ${currentMetricOption.label} - ${label}`,
          metricLabel: label
        });
      }}
      includeSummaryInfo
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={isLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      title={
        <Flex alignItems="center">
          <SlDropdownMenu
            buttonVariant="title"
            options={
              selectedGroupByFieldId === 'PromotionDealType'
                ? fieldOptions.filter((option) => option.id !== DorisMetricTypeEnum.PromotionSalesRetailSales)
                : fieldOptions
            }
            selectedId={selectedMainMetricField}
            onChange={(option) => setSelectedMainMetricField(option.id as DorisMetricTypeEnum)}
            defaultLabel="Promotion Sales"
          />
          <Text variant="h4" sx={{ margin: '0 8px' }}>
            by
          </Text>
          {groupByOptions.length > 1 ? (
            <SlDropdownMenu
              buttonVariant="title"
              options={groupByOptions}
              selectedId={selectedGroupByFieldId}
              onChange={(option) => setSelectedGroupByFieldId(option.id as DorisGroupByFieldNameEnum)}
              defaultLabel="Category"
            />
          ) : (
            <Text variant="h4">{currentGroupByOption.label}</Text>
          )}
        </Flex>
      }
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: comparisonData,
            categories: categoryData,
            metricType: currentMetricOption.metricType
          },
          {
            type: 'column',
            data: currentData,
            categories: categoryData,
            metricType: currentMetricOption.metricType
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2PromotionsDoubleDropdownTopEntityChart;
